import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  Globe2,
  AlertTriangle,
  CheckCircle2,
  Coins,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Banknote,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import HelpTooltip from "@/components/common/HelpTooltip";
import SectorScoresGrid from "@/components/market/SectorScoresGrid";
import { apiGet, formatLKR, formatPct, formatNumber, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const Stat = ({ label, value, sub, tone = "default", testId, helpKey }) => {
  const toneClass = {
    success: "text-[hsl(var(--success))]",
    danger: "text-[hsl(var(--destructive))]",
    warn: "text-amber-500",
    default: "",
  }[tone] || "";
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={testId}>
      <div className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground">
        <span>{label}</span>
        {helpKey && <HelpTooltip termId={helpKey} />}
      </div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, subtitle, right }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="h-5 w-5 text-primary" />}
      <div>
        <h2 className="text-lg font-display font-semibold">{title}</h2>
        {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
      </div>
    </div>
    {right}
  </div>
);

// Phase 7 (Item 25) — Foreign flow "View all" expandable. Lazy-loads the
// full per-company flow list from /api/market-intelligence/foreign-flows-all
// so the page doesn't pay the cost up-front for users who only glance at
// the top-3 cards above. Sorted by absolute net amount descending.
const ForeignFlowsViewAll = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (data || loading) return;
    setLoading(true);
    try {
      const r = await apiGet("/market-intelligence/foreign-flows-all");
      setData(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const rows = data?.rows || [];
  return (
    <Card className="border bg-card" data-testid="foreign-flows-view-all">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-primary" />
            All foreign flows
            {data?.date && (
              <Badge variant="secondary" className="text-[10px] font-normal">
                {formatDate(data.date)}
              </Badge>
            )}
          </CardTitle>
          <CardDescription className="text-[11px]">
            Every CSE company with foreign net activity in the latest CSE Daily PDF.
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={toggle}
          data-testid="foreign-flows-view-all-toggle"
        >
          {open ? (
            <><ChevronUp className="h-3.5 w-3.5 mr-1" /> Hide</>
          ) : (
            <><ChevronDown className="h-3.5 w-3.5 mr-1" /> View all →</>
          )}
        </Button>
      </CardHeader>
      {open && (
        <CardContent className="pt-2">
          {loading && (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          )}
          {err && !loading && (
            <div className="text-xs text-[hsl(var(--destructive))] italic">{err}</div>
          )}
          {!loading && !err && rows.length === 0 && (
            <div className="text-xs text-muted-foreground italic">
              No foreign flow rows on file. Upload the latest CSE Daily PDF
              to populate this list.
            </div>
          )}
          {!loading && !err && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="foreign-flows-all-table">
                <thead>
                  <tr className="border-b text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left py-2">Symbol</th>
                    <th className="text-left py-2">Company</th>
                    <th className="text-left py-2">Sector</th>
                    <th className="text-right py-2">Net amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const amt = r.amount_lkr_mn || 0;
                    const isBuy = (r.direction || "").includes("buy");
                    const tone = isBuy ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]";
                    return (
                      <tr
                        key={r.cse_symbol || idx}
                        className="border-b last:border-0 hover:bg-muted/30"
                        data-testid={`foreign-flow-row-${r.short_symbol || r.cse_symbol || idx}`}
                      >
                        <td className="py-2">
                          <Link
                            to={`/stocks/${r.short_symbol || r.company_code || ""}`}
                            className="font-mono text-xs text-[hsl(var(--primary))] hover:underline"
                          >
                            {r.cse_symbol || r.short_symbol}
                          </Link>
                        </td>
                        <td className="py-2 text-xs truncate max-w-[260px]">
                          {r.company_name || "—"}
                        </td>
                        <td className="py-2 text-[11px] text-muted-foreground">
                          {r.sector || "—"}
                        </td>
                        <td className={`py-2 text-right tabular-nums font-medium ${tone}`}>
                          {isBuy ? "+" : "-"}
                          {formatLKR(Math.abs(amt) * 1e6, { compact: true })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};


const MarketIntelligencePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useAuth();

  useEffect(() => {
    let active = true;
    apiGet("/market-intelligence/latest")
      .then((d) => { if (active) setData(d); })
      .catch((e) => console.warn(e))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="market-intel-loading">
        <Skeleton className="h-12 w-1/3" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-48" />
      </div>
    );
  }

  const daily = data?.daily;
  const macro = data?.macro;
  const cseDaily = data?.cse_daily;
  const brokerDaily = data?.broker_daily;
  const upcoming = data?.upcoming_dividends || [];
  const buys = data?.top_foreign_buys || [];
  const sells = data?.top_foreign_sells || [];
  const fundamentalsSummary = data?.fundamentals_summary;

  const hasAnyData = daily || macro;

  if (!hasAnyData) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="market-intel-empty">
        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h1 className="text-2xl font-display font-semibold mb-2">Market Intelligence</h1>
        <p className="text-sm text-muted-foreground mb-6">
          No market data uploaded yet. Upload the latest CSE Daily Market Summary or Capital Trust report to populate this dashboard.
        </p>
        {isAdmin && (
          <Link
            to="/admin/market-intelligence"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            data-testid="market-intel-empty-cta"
          >
            <FileText className="h-4 w-4" />
            Go to Market Intelligence Upload
          </Link>
        )}
      </div>
    );
  }

  const aspiTone = (daily?.aspi_change_pct ?? 0) >= 0 ? "success" : "danger";
  const aspiArrow = (daily?.aspi_change_pct ?? 0) >= 0 ? <ArrowUpRight className="h-4 w-4 inline" /> : <ArrowDownRight className="h-4 w-4 inline" />;
  const netForeign = daily?.net_foreign_flow_lkr_mn ?? brokerDaily?.net_foreign_flow_lkr_mn ?? null;
  const netForeignTone = (netForeign ?? 0) >= 0 ? "success" : "danger";

  return (
    <div className="space-y-8" data-testid="market-intel-page">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-display font-semibold">Market Intelligence</h1>
          {data?.daily_source === "cse_official_daily" && (
            <Badge variant="secondary" className="text-[10px]" data-testid="market-intel-source-badge">
              <CheckCircle2 className="h-3 w-3 mr-1" /> CSE Official
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {daily?.date && (
            <>Market data from {data?.daily_source === "cse_official_daily" ? "CSE Official Daily Report" : "Capital Trust Daily Market Watch"} — {formatDate(daily.date)}</>
          )}
        </p>
      </div>

      {/* SECTION 1 — Market Pulse */}
      <section className="space-y-3" data-testid="section-market-pulse">
        <SectionHeader icon={Activity} title="Market Pulse" subtitle="Latest snapshot of CSE market" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label="ASPI"
            value={daily?.aspi != null ? formatNumber(daily.aspi, 2) : "—"}
            sub={daily?.aspi_change_pct != null ? <>{aspiArrow} {formatPct(daily.aspi_change_pct)}</> : null}
            tone={aspiTone}
            helpKey="aspi"
            testId="stat-aspi"
          />
          <Stat
            label="S&P SL 20"
            value={daily?.sp20 != null ? formatNumber(daily.sp20, 2) : "—"}
            sub={daily?.sp20_change_pct != null ? formatPct(daily.sp20_change_pct) : null}
            helpKey="sp-sl-20"
            testId="stat-sp20"
          />
          <Stat
            label="Market PE"
            value={daily?.market_pe != null ? `${formatNumber(daily.market_pe, 2)}x` : "—"}
            helpKey="pe-ratio"
            testId="stat-pe"
          />
          <Stat
            label="Market PBV"
            value={daily?.market_pbv != null ? `${formatNumber(daily.market_pbv, 2)}x` : "—"}
            helpKey="pb-ratio"
            testId="stat-pbv"
          />
          <Stat
            label="Turnover"
            value={daily?.turnover_lkr_mn != null ? formatLKR(daily.turnover_lkr_mn * 1e6, { compact: true }) : (daily?.turnover_lkr != null ? formatLKR(daily.turnover_lkr, { compact: true }) : "—")}
            testId="stat-turnover"
          />
          <Stat
            label="Net Foreign Flow"
            value={netForeign != null ? formatLKR(netForeign * (data?.daily_source === "cse_official_daily" ? 1 : 1e6), { compact: true }) : "—"}
            sub={netForeign != null ? (netForeign >= 0 ? "Net buying" : "Net selling") : null}
            tone={netForeignTone}
            helpKey="foreign-net-flow"
            testId="stat-net-foreign"
          />
          <Stat
            label="Market RSI"
            value={daily?.market_rsi != null ? formatNumber(daily.market_rsi, 2) : "—"}
            sub={daily?.market_rsi != null ? (daily.market_rsi > 70 ? "Overbought" : daily.market_rsi < 30 ? "Oversold" : "Neutral") : null}
            testId="stat-rsi"
          />
          <Stat
            label="Traded Companies"
            value={daily?.traded_companies != null ? formatNumber(daily.traded_companies, 0) : "—"}
            testId="stat-traded"
          />
        </div>
        {fundamentalsSummary?.rows > 0 && (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <CheckCircle2 className="h-3 w-3 text-[hsl(var(--success))]" />
            {fundamentalsSummary.rows} broker fundamentals · week ending {formatDate(fundamentalsSummary.latest_week_ending)}
          </div>
        )}
      </section>

      <Separator />

      {/* SECTION 2 — Macro Indicators */}
      <section className="space-y-3" data-testid="section-macro">
        <SectionHeader icon={Banknote} title="Macro Indicators" subtitle={macro?.week_ending ? `Latest weekly update — week ending ${formatDate(macro.week_ending)}` : "Macro context"} />
        {macro ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="USD / LKR" value={macro.usd_lkr != null ? formatNumber(macro.usd_lkr, 2) : "—"} testId="stat-usd-lkr" />
              <Stat label="GBP / LKR" value={macro.gbp_lkr != null ? formatNumber(macro.gbp_lkr, 2) : "—"} testId="stat-gbp-lkr" />
              <Stat label="EUR / LKR" value={macro.eur_lkr != null ? formatNumber(macro.eur_lkr, 2) : "—"} testId="stat-eur-lkr" />
              <Stat label="AUD / LKR" value={macro.aud_lkr != null ? formatNumber(macro.aud_lkr, 2) : "—"} testId="stat-aud-lkr" />
              <Stat
                label="AWPR"
                value={macro.awpr != null ? `${formatNumber(macro.awpr, 2)}%` : "—"}
                helpKey="awpr"
                testId="stat-awpr"
              />
              <Stat
                label="T-bill 364d"
                value={macro.t_bill_364d != null ? `${formatNumber(macro.t_bill_364d, 2)}%` : "—"}
                helpKey="t-bill-yield"
                testId="stat-tbill"
              />
              <Stat
                label="FX Reserves"
                value={macro.fx_reserves_usd_bn != null ? `$${formatNumber(macro.fx_reserves_usd_bn, 2)}B` : "—"}
                sub="USD"
                helpKey="fx-reserves"
                testId="stat-fx-reserves"
              />
              <Stat
                label="Remittances"
                value={macro.remittances_usd_mn != null ? `$${formatNumber(macro.remittances_usd_mn, 0)}M` : "—"}
                sub="USD"
                testId="stat-remittances"
              />
              <Stat
                label="Manufacturing PMI"
                value={macro.manufacturing_pmi != null ? formatNumber(macro.manufacturing_pmi, 1) : "—"}
                sub={macro.manufacturing_pmi != null ? (macro.manufacturing_pmi >= 50 ? "Expansion" : "Contraction") : null}
                tone={macro.manufacturing_pmi >= 50 ? "success" : (macro.manufacturing_pmi != null ? "danger" : "default")}
                helpKey="pmi"
                testId="stat-pmi-mfg"
              />
              <Stat
                label="Services PMI"
                value={macro.services_pmi != null ? formatNumber(macro.services_pmi, 1) : "—"}
                sub={macro.services_pmi != null ? (macro.services_pmi >= 50 ? "Expansion" : "Contraction") : null}
                tone={macro.services_pmi >= 50 ? "success" : (macro.services_pmi != null ? "danger" : "default")}
                helpKey="pmi"
                testId="stat-pmi-svc"
              />
              <Stat
                label="IMF Status"
                value={macro.imf_status ? macro.imf_status.replace(/_/g, " ") : "—"}
                sub={macro.imf_status_text}
                testId="stat-imf"
              />
              <Stat
                label="Net Foreign (week)"
                value={macro.net_foreign_flow_week_lkr_mn != null ? formatLKR(macro.net_foreign_flow_week_lkr_mn * 1e6, { compact: true }) : "—"}
                tone={(macro.net_foreign_flow_week_lkr_mn ?? 0) >= 0 ? "success" : "danger"}
                helpKey="foreign-net-flow"
                testId="stat-foreign-week"
              />
            </div>

            {macro.economic_summary && (
              <Card className="mt-3">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Economic Summary</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground leading-relaxed">
                  {macro.economic_summary}
                </CardContent>
              </Card>
            )}
            {(macro.key_risks?.length || macro.key_opportunities?.length) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {macro.key_risks?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        Key Risks
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-1.5">
                      {macro.key_risks.map((r, i) => <div key={i}>• {r}</div>)}
                    </CardContent>
                  </Card>
                )}
                {macro.key_opportunities?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
                        Key Opportunities
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-1.5">
                      {macro.key_opportunities.map((r, i) => <div key={i}>• {r}</div>)}
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : null}
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-4 text-sm" data-testid="macro-empty-state">
            <div className="font-medium mb-1">No macro update available yet</div>
            <div className="text-xs text-muted-foreground leading-relaxed mb-3">
              Upload the latest <strong>Capital Trust Weekly Market Update</strong> PDF to populate
              AWPR, T-bill yields, FX Reserves, USD/LKR, PMIs, remittances and the IMF tracker.
            </div>
            {isAdmin && (
              <Link
                to="/admin/market-intelligence"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                data-testid="macro-empty-upload-cta"
              >
                <FileText className="h-3.5 w-3.5" />
                Go to Market Intelligence upload
              </Link>
            )}
          </div>
        )}
      </section>

      <Separator />

      {/* SECTION 3 — Layer 6: Sector Scores (Claude weekly) */}
      <section className="space-y-3" data-testid="section-sector-scores">
        <SectionHeader
          icon={Activity}
          title="Sector Scores · Layer 6"
          subtitle="Claude analyses each CSE sector once a week (Sundays 21:00) on cycle, valuation, macro tailwinds, and foreign sentiment. Each stock's COMB score picks up a 0-10 sector contribution."
        />
        <SectorScoresGrid isAdmin={isAdmin} />
      </section>

      <Separator />

      {/* SECTION 4 — Foreign Flow Tracker */}
      <section className="space-y-3" data-testid="section-foreign-flow">
        <SectionHeader
          icon={Globe2}
          title="Foreign Flow Tracker"
          subtitle={`Latest broker daily — ${brokerDaily?.date ? formatDate(brokerDaily.date) : "upload broker daily for foreign flow detail"}`}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card data-testid="foreign-buys-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[hsl(var(--success))]" />
                Foreigners Buying
              </CardTitle>
            </CardHeader>
            <CardContent>
              {buys.length === 0 ? (
                <div className="text-xs text-muted-foreground italic">No data — upload broker daily.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {buys.slice(0, 3).map((b) => (
                    <li key={b.symbol} className="py-2 flex items-center justify-between gap-2">
                      <Link
                        to={`/stocks/${b.short_symbol || b.symbol?.split(".")[0] || ""}`}
                        className="text-sm font-medium hover:underline"
                      >
                        {b.short_symbol || b.symbol}
                      </Link>
                      <span className="text-xs text-muted-foreground truncate">{b.company_name}</span>
                      <span className="text-sm font-medium tabular-nums text-[hsl(var(--success))] ml-auto">
                        +{formatLKR((b.amount_lkr_mn || 0) * 1e6, { compact: true })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <Card data-testid="foreign-sells-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-[hsl(var(--destructive))]" />
                Foreigners Selling
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sells.length === 0 ? (
                <div className="text-xs text-muted-foreground italic">No data — upload broker daily.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {sells.slice(0, 3).map((s) => (
                    <li key={s.symbol} className="py-2 flex items-center justify-between gap-2">
                      <Link
                        to={`/stocks/${s.short_symbol || s.symbol?.split(".")[0] || ""}`}
                        className="text-sm font-medium hover:underline"
                      >
                        {s.short_symbol || s.symbol}
                      </Link>
                      <span className="text-xs text-muted-foreground truncate">{s.company_name}</span>
                      <span className="text-sm font-medium tabular-nums text-[hsl(var(--destructive))] ml-auto">
                        -{formatLKR((s.amount_lkr_mn || 0) * 1e6, { compact: true })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Phase 7 (Item 25) — "View all" expandable: full list of every
            company with foreign activity in the latest CSE Daily PDF. */}
        <ForeignFlowsViewAll />
      </section>

      <Separator />

      {/* SECTION 5 — Upcoming Dividends */}
      <section className="space-y-3" data-testid="section-dividends">
        <SectionHeader icon={Coins} title="Upcoming Dividends" subtitle="Gross / net (after 10% withholding tax)" />
        {upcoming.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">No upcoming dividends in the next 60 days.</div>
        ) : (
          <div className="rounded-lg border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Symbol</th>
                  <th className="px-3 py-2 text-right">
                    Gross / Net DPS
                    <HelpTooltip termId="net-dividend" />
                  </th>
                  <th className="px-3 py-2 text-left">XD Date</th>
                  <th className="px-3 py-2 text-left">Payment Date</th>
                  <th className="px-3 py-2 text-right">Days to XD</th>
                </tr>
              </thead>
              <tbody>
                {upcoming.map((d, i) => {
                  const xd = d.ex_dividend_date || d.xd_date;
                  const days = xd ? Math.round((new Date(xd) - new Date()) / 86400000) : null;
                  const code = d.company_code || (d.cse_symbol || "").split(".")[0];
                  return (
                    <tr key={`${d.cse_symbol}-${xd}-${i}`} className="border-t border-border">
                      <td className="px-3 py-2">
                        <Link to={`/stocks/${code}`} className="font-medium hover:underline" data-testid={`dividend-row-${code}`}>
                          {code}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div>Rs. {Number(d.dps_gross || d.dividend_per_share || 0).toFixed(2)} <span className="text-muted-foreground">gross</span></div>
                        <div className="text-xs text-muted-foreground">Rs. {Number(d.dps_net ?? ((d.dps_gross || d.dividend_per_share || 0) * 0.9)).toFixed(2)} net (after 10% WHT)</div>
                      </td>
                      <td className="px-3 py-2">{xd ? formatDate(xd) : "—"}</td>
                      <td className="px-3 py-2">{d.payment_date ? formatDate(d.payment_date) : "—"}</td>
                      <td className="px-3 py-2 text-right">
                        {days != null ? (
                          <Badge variant={days <= 3 ? "destructive" : days <= 14 ? "secondary" : "outline"}>
                            {days >= 0 ? `${days} days` : "passed"}
                          </Badge>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default MarketIntelligencePage;
